import type { RouteLayoutHandle } from '@/app/routeLayout';
import { Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet, type RouteObject } from 'react-router-dom';

import { RequireAuth } from '@/components/auth/RequireAuth';
import { RequireMFAVerified } from '@/components/auth/RequireMFAVerified';
import { RequireAnyPermission } from '@/components/auth/RequireAnyPermission';
import { RequirePermission } from '@/components/auth/RequirePermission';
import { AppLayout } from '@/components/layout/AppLayout';
import { RouteErrorBoundary } from '@/components/layout/RouteErrorBoundary';
import { AuthCallbackPage } from '@/features/auth/AuthCallbackPage';
import { LoginPage } from '@/features/auth/LoginPage';
import { MfaChallengePage } from '@/features/auth/MfaChallengePage';
import { ResetPasswordPage } from '@/features/auth/ResetPasswordPage';
import { UpdatePasswordPage } from '@/features/auth/UpdatePasswordPage';
import { NotFoundPage } from '@/features/shared/NotFoundPage';
import { NoAccessPage } from '@/features/shared/NoAccessPage';
import { ProjectsAccessGuard } from '@/features/projects/acceso/ProjectsAccessGuard';
import { PERMISO_PRIVACIDAD } from '@/features/privacidad/permiso';
import {
  AIHubPage, AgentDetailPage, AiConfigAdminPage, AuditAdminPage, BacklogItemDetailPage,
  BacklogsPage, BackupsAdminPage, CalendarSettingsPage, CampaignDetailPage, CampaignFormPage,
  CampaignWizardPage, CampaignsListPage, CaseStudiesPage, CaseStudyDetailPage, CatalogPage, ChannelRulesPage, ChatPage,
  ClientEnvironmentDetailPage, ClientEnvironmentsPage, CollectionsPage, ConnectionsPage,
  ContactQueuePage, ContractsPage, CuentasParaCobrarPage, DashboardPage, GastosPage, DirectorioPage, EmployeeDetailPage, EmployeesHubPage,
  FromJobPage, HelpCenterPage, InfraPage, InvoicesPage,
  InvoicingAdminPage, LauncherPage, LauncherSettingsPage, LeadsListPage, MyAiContextPage,
  NewProjectPage, NewsletterFormPage, NewsletterListPage, NotificationsPage, OrganizationPage,
  PartnerDetailPage, PartnersPage, ProfilePage, ProjectBacklogTab, ProjectChatTab,
  ProjectDecisionsTab, ProjectDetailPage, ProjectDocsTab, ProjectEdgeFunctionsTab,
  ProjectIntegrationsTab, ProjectJournalTab, ProjectMeetingsTab, ProjectOverviewTab,
  ProjectPdfsTab, ProjectStackTab, ProjectStagesTab, ProjectSupportTab, ProjectWorkersTab,
  ProjectWorkflowsTab, ProjectsListPage, ProspectDetailPage, ProspectFormPage,
  PrivacidadPage, ProspectWizardPage, ProspectsListPage, SecurityPage, SettingsHomePage, ShareProspectsPage,
  ConexionesIaPage, SignaturesPage, SolActivityPage, SolChatsPage, SoporteMetricasPage, TaskDetailPage, TasksHubPage,
  TelegramPage,
} from '@/app/paginas';
import type { AppPermission } from '@/types/app-permissions';


/**
 * Pequeño helper para envolver una page con un guard de permiso opcional.
 * Si el permiso no está, devuelve un placeholder de "sin acceso" en lugar
 * de redirect — más útil para entender por qué un usuario no ve algo.
 */
function GuardedPage({
  permission,
  anyOf,
  children,
}: {
  permission?: AppPermission;
  /** Alcanza con cualquiera de estos permisos (pantallas que juntan partes con permisos distintos). */
  anyOf?: AppPermission[];
  children: React.ReactNode;
}) {
  if (anyOf) {
    return (
      <RequireAnyPermission permissions={anyOf} fallback={<NoAccessPage />}>
        {children}
      </RequireAnyPermission>
    );
  }
  if (!permission) return <>{children}</>;
  return (
    <RequirePermission
      permission={permission}
      fallback={<NoAccessPage />}
    >
      {children}
    </RequirePermission>
  );
}

/**
 * Exportado para el test que recorre las rutas y verifica que cada hoja
 * resuelva a un tipo de pantalla declarado o heredado. "Nos olvidamos de una
 * ruta" pasa a ser un test rojo en vez de una pantalla mal armada.
 */
export const routes: RouteObject[] = [
  // Root catch-all error boundary — atrapa errores que escapan a rutas
  // hijas o errores de carga inicial. Cada layout interno puede sobrescribir
  // con su propio errorElement si quiere copy contextual distinto.
  { path: '/', element: <Navigate to="/dashboard" replace />, errorElement: <RouteErrorBoundary /> },
  { path: '/login', element: <LoginPage />, errorElement: <RouteErrorBoundary /> },
  // Auth flows públicos · NO requieren sesión previa.
  // /auth/callback maneja magic links (invite, recovery, email_change)
  // /auth/reset-password muestra form para iniciar password recovery
  // /auth/update-password se llega vía /auth/callback con sesión efímera
  { path: '/auth/callback', element: <AuthCallbackPage />, errorElement: <RouteErrorBoundary /> },
  { path: '/auth/reset-password', element: <ResetPasswordPage />, errorElement: <RouteErrorBoundary /> },
  { path: '/auth/update-password', element: <UpdatePasswordPage />, errorElement: <RouteErrorBoundary /> },
  { path: '/auth/mfa-challenge', element: <MfaChallengePage />, errorElement: <RouteErrorBoundary /> },
  {
    // Launcher post-login · autenticado pero SIN el shell del sidebar
    // (pantalla full-screen de accesos rápidos). Usa el mismo guard de auth+MFA
    // que la app, vía <Outlet/> en vez de <AppLayout/>.
    element: (
      <RequireAuth>
        <RequireMFAVerified>
          {/* El launcher también es diferido y acá no está el shell que lo espera. */}
          <Suspense fallback={null}>
            <Outlet />
          </Suspense>
        </RequireMFAVerified>
      </RequireAuth>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [{ path: '/launcher', element: <LauncherPage /> }],
  },
  {
    // Toda la app autenticada · RequireAuth bloquea sin sesión.
    // RequireMFAVerified intercepta cualquier user que tenga MFA enrolled
    // y aún no haya completado el challenge (currentLevel='aal1' con
    // nextLevel='aal2'). Patrón clásico: login → 2FA → app.
    // errorElement atrapa cualquier error que tire una ruta hija — en lugar
    // del cartel default de react-router, muestra el RouteErrorBoundary
    // amigable con opciones claras (recargar / volver al inicio / contacto).
    element: (
      <RequireAuth>
        <RequireMFAVerified>
          <AppLayout />
        </RequireMFAVerified>
      </RequireAuth>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      { path: '/dashboard', element: <DashboardPage /> },
      {
        path: '/prospects',
        handle: { layout: 'work' } satisfies RouteLayoutHandle,
        element: (
          <GuardedPage permission="prospects.read.own">
            <ProspectsListPage />
          </GuardedPage>
        ),
      },
      {
        path: '/prospects/new',
        element: (
          <GuardedPage permission="prospects.write.own">
            <ProspectFormPage mode="create" />
          </GuardedPage>
        ),
      },
      {
        // Wizard IA de alta de prospects, página dedicada full-width.
        // ?mode=manual arranca directo en el form sin IA (atajo).
        path: '/prospects/new/ai',
        element: (
          <GuardedPage permission="prospects.write.own">
            <ProspectWizardPage />
          </GuardedPage>
        ),
      },
      {
        // Conversión de un deep search job (async) en un prospect — toma
        // el result_json del job y abre form pre-llenado para review + save.
        path: '/prospects/new/from-job/:jobId',
        element: (
          <GuardedPage permission="prospects.write.own">
            <FromJobPage />
          </GuardedPage>
        ),
      },
      {
        // Cola de verificación de contactos. Va antes de /prospects/:id para
        // que se lea el orden real de matcheo de un vistazo.
        path: '/prospects/contactos',
        handle: { layout: 'work' } satisfies RouteLayoutHandle,
        element: (
          <GuardedPage permission="prospects.read.own">
            <ContactQueuePage />
          </GuardedPage>
        ),
      },
      {
        // Directorio: nombre, dueño y etapa de todas las fichas. Antes de
        // /prospects/:id para que «directorio» no se lea como un id.
        path: '/prospects/directorio',
        element: (
          <GuardedPage permission="prospects.directory.view">
            <DirectorioPage />
          </GuardedPage>
        ),
      },
      {
        path: '/prospects/:id',
        // Excepción deliberada: NO es `work`. La ficha tiene pestañas sticky y
        // secciones largas, y el desplazamiento tiene que quedarse en la página
        // para que los enlaces profundos a una sección funcionen y para que las
        // pestañas se peguen bajo la topbar y no dentro de un panel.
        handle: { layout: 'document' } satisfies RouteLayoutHandle,
        element: (
          <GuardedPage permission="prospects.read.own">
            <ProspectDetailPage />
          </GuardedPage>
        ),
      },
      {
        path: '/prospects/:id/edit',
        element: (
          <GuardedPage permission="prospects.write.own">
            <ProspectFormPage mode="edit" />
          </GuardedPage>
        ),
      },
      {
        // Wizard de generación de campaña IA, página dedicada (no modal).
        // Opción B de UI: container full-width para que el step Preview (10 emails A/B)
        // tenga espacio real. Header con breadcrumb al detalle del prospect.
        path: '/prospects/:id/campaign/new',
        element: (
          <GuardedPage permission="campaigns.write.own">
            <CampaignWizardPage />
          </GuardedPage>
        ),
      },
      {
        // Pantalla de trabajo: tres columnas que ocupan la ventana y desplazan
        // por dentro. Sus hijas de tipo documento (/campaigns/new,
        // /campaigns/:id/edit) NO lo heredan — gana la declaración más profunda
        // y ellas no declaran nada, así que caen al default `document`.
        path: '/campaigns',
        handle: { layout: 'work' } satisfies RouteLayoutHandle,
        element: (
          <GuardedPage permission="campaigns.read.own">
            <CampaignsListPage />
          </GuardedPage>
        ),
      },
      {
        path: '/campaigns/newsletters',
        handle: { layout: 'work' } satisfies RouteLayoutHandle,
        element: (
          <GuardedPage permission="newsletters.manage">
            <NewsletterListPage />
          </GuardedPage>
        ),
      },
      {
        path: '/campaigns/newsletters/new',
        element: (
          <GuardedPage permission="newsletters.manage">
            <NewsletterFormPage mode="create" />
          </GuardedPage>
        ),
      },
      {
        path: '/campaigns/newsletters/:id',
        element: (
          <GuardedPage permission="newsletters.manage">
            <NewsletterFormPage mode="edit" />
          </GuardedPage>
        ),
      },
      {
        path: '/campaigns/new',
        element: (
          <GuardedPage permission="campaigns.write.own">
            <CampaignFormPage mode="create" />
          </GuardedPage>
        ),
      },
      {
        path: '/campaigns/:id',
        element: (
          <GuardedPage permission="campaigns.read.own">
            <CampaignDetailPage />
          </GuardedPage>
        ),
      },
      {
        path: '/campaigns/:id/edit',
        element: (
          <GuardedPage permission="campaigns.write.own">
            <CampaignFormPage mode="edit" />
          </GuardedPage>
        ),
      },
      {
        path: '/connections',
        element: (
          <GuardedPage permission="campaigns.write.own">
            <ConnectionsPage />
          </GuardedPage>
        ),
      },
      {
        path: '/leads',
        handle: { layout: 'work' } satisfies RouteLayoutHandle,
        element: (
          <GuardedPage permission="leads.read.own">
            <LeadsListPage />
          </GuardedPage>
        ),
      },
      {
        // El detalle es un PANEL de /leads, no una pantalla aparte. La ruta se
        // conserva para que los enlaces profundos —los que manda «Respondió» de
        // una campaña social, por ejemplo— sigan abriendo la conversación: monta
        // el mismo listado con esa conversación seleccionada.
        path: '/leads/:id',
        handle: { layout: 'work' } satisfies RouteLayoutHandle,
        element: (
          <GuardedPage permission="leads.read.own">
            <LeadsListPage />
          </GuardedPage>
        ),
      },
      {
        path: '/organization',
        element: (
          <GuardedPage permission="organization.read.all">
            <OrganizationPage />
          </GuardedPage>
        ),
      },
      {
        path: '/cuentas-para-cobrar',
        handle: { layout: 'document' } satisfies RouteLayoutHandle,
        element: (
          <GuardedPage permission="collections.manage">
            <CuentasParaCobrarPage />
          </GuardedPage>
        ),
      },
      // Gastos fijos (change gastos-fijos): cuentas de pago, servicios, facturas
      // y rentabilidad por cliente. expenses.manage: admin_dendev por seed,
      // superadmin por bypass. La RLS de las cuatro tablas pide lo mismo.
      {
        path: '/gastos',
        handle: { layout: 'document' } satisfies RouteLayoutHandle,
        element: (
          <GuardedPage permission="expenses.manage">
            <GastosPage />
          </GuardedPage>
        ),
      },
      {
        path: '/catalog',
        handle: { layout: 'work' } satisfies RouteLayoutHandle,
        element: (
          <GuardedPage permission="catalog.read.all">
            <CatalogPage />
          </GuardedPage>
        ),
      },
      {
        // Hub para IA · módulo unificado de gobernanza IA
        // (agentes, reglas, denylist, contexto, controles)
        path: '/ai-hub',
        handle: { layout: 'work' } satisfies RouteLayoutHandle,
        element: (
          <GuardedPage permission="ai_rules.read.all">
            <AIHubPage />
          </GuardedPage>
        ),
      },
      {
        // Detalle + edición de un agente individual
        path: '/ai-hub/agents/:agentKey',
        element: (
          <GuardedPage permission="ai_rules.read.all">
            <AgentDetailPage />
          </GuardedPage>
        ),
      },
      // Rutas legacy → redirect al hub con tab correspondiente
      { path: '/ai-rules', element: <Navigate to="/ai-hub?tab=rules" replace /> },
      { path: '/denylist', element: <Navigate to="/ai-hub?tab=denylist" replace /> },
      // Personas · hub con tabs Directorio | Distribución | Áreas | Cargos |
      // Cuentas | Roles + perfil empleado (Anexo A Fase A). Las rutas legacy
      // /admin/areas, /admin/cargos, /admin/users y /admin/roles redirigen al
      // hub con la tab correspondiente.
      {
        path: '/equipo',
        handle: { layout: 'work' } satisfies RouteLayoutHandle,
        element: (
          <GuardedPage anyOf={['employees.view', 'users.manage']}>
            <EmployeesHubPage />
          </GuardedPage>
        ),
      },
      {
        // Permiso evaluado adentro de la página: el user puede ver su
        // propio perfil aunque no tenga employees.view (ver
        // EmployeeDetailPage). Quien tenga employees.view ve cualquiera.
        path: '/equipo/:userId',
        element: <EmployeeDetailPage />,
      },
      { path: '/admin/areas', element: <Navigate to="/equipo?tab=areas" replace /> },
      { path: '/admin/cargos', element: <Navigate to="/equipo?tab=cargos" replace /> },
      // Tareas · Anexo A Fase B CORTE 1 · backlog interno con realtime.
      {
        path: '/tareas',
        handle: { layout: 'work' } satisfies RouteLayoutHandle,
        element: (
          <GuardedPage permission="tasks.view">
            <TasksHubPage />
          </GuardedPage>
        ),
      },
      {
        path: '/tareas/:id',
        handle: { layout: 'work' } satisfies RouteLayoutHandle,
        element: (
          <GuardedPage permission="tasks.view">
            <TaskDetailPage />
          </GuardedPage>
        ),
      },
      // Chat interno · Fase B-2 (Slack-local) · canales + DMs + mentions.
      {
        path: '/chat',
        handle: { layout: 'work' } satisfies RouteLayoutHandle,
        element: (
          <GuardedPage permission="messages.send">
            <ChatPage />
          </GuardedPage>
        ),
      },
      // Sol · apartado personal de conversaciones con la IA + buscador.
      // Sin permiso: todo usuario autenticado tiene a Sol. RLS owner-only.
      {
        path: '/sol',
        handle: { layout: 'work' } satisfies RouteLayoutHandle,
        element: <SolChatsPage />,
      },
      // Casos de éxito · biblioteca de la experiencia con clientes propios.
      // Ver: case_studies.view; escribir: case_studies.manage (RLS lo exige igual).
      {
        path: '/casos-de-exito',
        handle: { layout: 'work' } satisfies RouteLayoutHandle,
        element: (
          <GuardedPage permission="case_studies.view">
            <CaseStudiesPage />
          </GuardedPage>
        ),
      },
      {
        path: '/casos-de-exito/:id',
        handle: { layout: 'work' } satisfies RouteLayoutHandle,
        element: (
          <GuardedPage permission="case_studies.view">
            <CaseStudyDetailPage />
          </GuardedPage>
        ),
      },
      // Projects · pipeline prospect→client→project (mayo 2026)
      {
        path: '/proyectos',
        handle: { layout: 'work' } satisfies RouteLayoutHandle,
        element: (
          <ProjectsAccessGuard>
            <ProjectsListPage />
          </ProjectsAccessGuard>
        ),
      },
      // Backlogs · vista consolidada de los .backlog/ de cada repo. Solo
      // lectura: la fuente de verdad son los Markdown, el sync es de una vía.
      // backlogs.view: employee y admin_dendev (y el superadmin por bypass).
      {
        path: '/backlogs',
        handle: { layout: 'work' } satisfies RouteLayoutHandle,
        element: (
          <GuardedPage permission="backlogs.view">
            <BacklogsPage />
          </GuardedPage>
        ),
        children: [
          { path: ':sourceKey/:itemId', element: <BacklogItemDetailPage /> },
        ],
      },

      {
        path: '/proyectos/nuevo',
        element: (
          <GuardedPage permission="projects.create">
            <NewProjectPage />
          </GuardedPage>
        ),
      },
      {
        // Workspace de proyecto · Anexo B Fase W1.
        // ProjectDetailPage es el LAYOUT (hero + properties + tabs + Outlet).
        // Cada child route monta una tab. Cambiar de tab no remonta el shell
        // ni re-fetchea el proyecto.
        path: '/proyectos/:id',
        handle: { layout: 'work' } satisfies RouteLayoutHandle,
        element: (
          <ProjectsAccessGuard>
            <ProjectDetailPage />
          </ProjectsAccessGuard>
        ),
        children: [
          { index: true, element: <Navigate to="overview" replace /> },
          { path: 'overview', element: <ProjectOverviewTab /> },
          // Hub de Proyectos · ciclo de vida + memoria del proyecto (jul 2026)
          { path: 'etapas', element: <ProjectStagesTab /> },
          { path: 'bitacora', element: <ProjectJournalTab /> },
          { path: 'reuniones', element: <ProjectMeetingsTab /> },
          { path: 'decisiones', element: <ProjectDecisionsTab /> },
          { path: 'stack', element: <ProjectStackTab /> },
          { path: 'docs', element: <ProjectDocsTab /> },
          { path: 'backlog', element: <ProjectBacklogTab /> },
          // El ítem de un repo asociado al proyecto se lee DENTRO del proyecto
          // (regla 2026-09-26): /backlogs es para lo que no tiene proyecto.
          { path: 'backlog/:sourceKey/:itemId', element: <BacklogItemDetailPage /> },
          { path: 'soporte', element: <ProjectSupportTab /> },
          // Integraciones · una tabla, distintos filtros vía sub-rutas.
          { path: 'workflows', element: <ProjectWorkflowsTab /> },
          { path: 'workers', element: <ProjectWorkersTab /> },
          { path: 'edge', element: <ProjectEdgeFunctionsTab /> },
          { path: 'integrations', element: <ProjectIntegrationsTab /> },
          { path: 'chat', element: <ProjectChatTab /> },
          { path: 'pdfs', element: <ProjectPdfsTab /> },
        ],
      },
      // Facturación · Factura C de monotributo vía ARCA (FACT-01). El acceso
      // hoy es solo superadmin (bypass — invoices.* sin seed de role_permissions,
      // spec §4.7).
      {
        path: '/facturacion',
        handle: { layout: 'work' } satisfies RouteLayoutHandle,
        element: (
          <GuardedPage permission="invoices.view">
            <InvoicesPage />
          </GuardedPage>
        ),
      },
      // Cobranzas & Comisiones (Política de Comisiones v1.0). Vendedor entra
      // con collections.view.own (override); admin_dendev lo tiene seedeado.
      {
        path: '/cobranzas',
        handle: { layout: 'work' } satisfies RouteLayoutHandle,
        element: (
          <GuardedPage permission="collections.view.own">
            <CollectionsPage />
          </GuardedPage>
        ),
      },
      // La miga de pan enlaza «Soporte»: sin esto, ese segmento llevaba a un 404.
      { path: '/soporte', element: <Navigate to="/soporte/metricas" replace /> },
      // Métricas de soporte (change soporte-metricas-de-respuesta): tiempos de
      // respuesta y desempeño por persona. support.metrics.view: admin_dendev
      // por seed, superadmin por bypass, empleados no. La RPC lo exige igual.
      {
        path: '/soporte/metricas',
        handle: { layout: 'document' } satisfies RouteLayoutHandle,
        element: (
          <GuardedPage permission="support.metrics.view">
            <SoporteMetricasPage />
          </GuardedPage>
        ),
      },
      // Contratos & Anexos · sección legal-documental (spec 2026-08-18). El
      // vendedor ve su cartera; la carga de docs/montos es collections.manage.
      {
        path: '/contratos',
        handle: { layout: 'work' } satisfies RouteLayoutHandle,
        element: (
          <GuardedPage permission="collections.view.own">
            <ContractsPage />
          </GuardedPage>
        ),
      },
      // Partners · administración del servicio recurrente de portal.dendev.ar
      // (change partners-administracion-del-servicio). El permiso de core
      // gobierna la ruta; la autoridad sobre los datos la da el perfil
      // dendev_admin del schema portal (AccessGate adentro de la página).
      {
        path: '/partners',
        handle: { layout: 'work' } satisfies RouteLayoutHandle,
        element: (
          <GuardedPage permission="partners.view">
            <PartnersPage />
          </GuardedPage>
        ),
      },
      {
        path: '/partners/:slug',
        handle: { layout: 'work' } satisfies RouteLayoutHandle,
        element: (
          <GuardedPage permission="partners.view">
            <PartnerDetailPage />
          </GuardedPage>
        ),
      },
      // Rutas /admin/* · MFA enforcement ya lo hace RequireMFAVerified arriba
      // a nivel de toda la app autenticada. Acá solo permission guards.
      {
        // Config de ARCA (cert/key/PV). Gate de ruta por invoices.manage; el
        // guard duro superadmin vive dentro de la página + lo revalida el edge.
        path: '/admin/facturacion',
        element: (
          <GuardedPage permission="invoices.manage">
            <InvoicingAdminPage />
          </GuardedPage>
        ),
      },
      // Cuentas y roles viven en Personas (/equipo), cada una en su pestaña.
      { path: '/admin/users', element: <Navigate to="/equipo?tab=cuentas" replace /> },
      { path: '/admin/roles', element: <Navigate to="/equipo?tab=roles" replace /> },
      {
        path: '/admin/feature-flags',
        // Pantalla retirada: las funciones en prueba no se administran desde la interfaz.
        element: <Navigate to="/admin/infraestructura" replace />,
      },
      {
        // Reglas de formato por red social (límite de caracteres + tono).
        // Tres capas: guard acá, entrada de nav condicionada al permiso, y la
        // policy de RLS de core.social_channel_rules.
        path: '/admin/reglas-por-red',
        handle: { layout: 'work' } satisfies RouteLayoutHandle,
        element: (
          <GuardedPage permission="campaigns.read.own">
            <ChannelRulesPage />
          </GuardedPage>
        ),
      },
      {
        path: '/admin/ai-config',
        element: (
          <GuardedPage permission="telegram.users.manage">
            <AiConfigAdminPage />
          </GuardedPage>
        ),
      },
      // Actividad de Sol (observabilidad F2 · Anexo F). El permiso es el mismo
      // que habilita la RLS de rag_chat_threads/messages para lectura cross-user
      // (agent_logs.read.all desde 20261028120300: con prospects.read.all la
      // veía también un directivo, y la actividad de Sol es gobernanza de IA).
      {
        path: '/admin/sol',
        handle: { layout: 'work' } satisfies RouteLayoutHandle,
        element: (
          <GuardedPage permission="agent_logs.read.all">
            <SolActivityPage />
          </GuardedPage>
        ),
      },
      // Clientes · entornos de cliente (Anexo H · H4). El acceso hoy es
      // solo superadmin (bypass — sin seed de role_permissions, §15.1).
      {
        // El detalle se anida para que la lista y la ficha convivan en una sola
        // pantalla maestro-detalle: antes eran dos rutas y volver perdía el
        // contexto de la lista.
        path: '/admin/clientes',
        handle: { layout: 'work' } satisfies RouteLayoutHandle,
        element: (
          <GuardedPage permission="clients.view">
            <ClientEnvironmentsPage />
          </GuardedPage>
        ),
        children: [
          {
            path: ':id',
            element: <ClientEnvironmentDetailPage />,
          },
        ],
      },
      {
        path: '/admin/audit',
        handle: { layout: 'work' } satisfies RouteLayoutHandle,
        element: (
          <GuardedPage permission="audit.read">
            <AuditAdminPage />
          </GuardedPage>
        ),
      },
      {
        // Backups · solo super admin. El guard duro vive dentro de la página
        // (chequea isSuperadmin del JWT). Acá usamos audit.read como gate
        // mínimo a nivel de ruta para evitar render para roles no-admin.
        path: '/admin/backups',
        element: (
          <GuardedPage permission="audit.read">
            <BackupsAdminPage />
          </GuardedPage>
        ),
      },
      // Infraestructura (change infra-observabilidad-base): lo que el bot de
      // logs puede decir, con más detalle. infra.view no está en ningún rol a
      // propósito → solo superadmin por bypass, como clients.*.
      {
        path: '/admin/infraestructura',
        handle: { layout: 'work' } satisfies RouteLayoutHandle,
        element: (
          <GuardedPage permission="infra.view">
            <InfraPage />
          </GuardedPage>
        ),
      },
      // Privacidad (change privacidad-del-diagnostico): borrado a pedido,
      // retención de 24 meses e historial. privacy.manage no está en ningún rol
      // a propósito → superadmin por bypass, o por override.
      {
        path: '/privacidad',
        element: (
          <GuardedPage permission={PERMISO_PRIVACIDAD}>
            <PrivacidadPage />
          </GuardedPage>
        ),
      },
      // Configuración vuelve al planteo original (25/08): /settings es el
      // índice de tarjetas y cada sección es una página de documento suelta.
      // En el medio hubo dos intentos de navegación persistente —un segundo
      // menú vertical pegado al sidebar y después una fila de pestañas— y los
      // dos perdieron contra el índice: nueve secciones que se visitan poco no
      // ameritan navegación siempre a la vista, y la grilla original ya traía
      // el estado de cada sección (2FA, firmas, conexiones) que ninguna
      // navegación compacta puede mostrar.
      { path: '/settings', element: <SettingsHomePage /> },
      { path: '/settings/profile', element: <ProfilePage /> },
      { path: '/settings/signatures', element: <SignaturesPage /> },
      {
        // Pasó a Finanzas (/cuentas-para-cobrar): el enlace viejo sigue andando.
        path: '/settings/cuentas-para-cobrar',
        element: <Navigate to="/cuentas-para-cobrar" replace />,
      },
      { path: '/settings/security', element: <SecurityPage /> },
      { path: '/settings/telegram', element: <TelegramPage /> },
      { path: '/settings/notifications', element: <NotificationsPage /> },
      { path: '/settings/launcher', element: <LauncherSettingsPage /> },
      { path: '/settings/calendario', element: <CalendarSettingsPage /> },
      // Auto-compartir prospectos · regla persistente "compartir siempre
      // con X". Cualquier usuario logueado gestiona sus propias reglas.
      { path: '/settings/compartir-prospectos', element: <ShareProspectsPage /> },
      {
        // Contexto IA personal · memoria persistente per-user.
        path: '/settings/mi-ia',
        element: (
          <GuardedPage permission="user_ai_context.read.own">
            <MyAiContextPage />
          </GuardedPage>
        ),
      },
      {
        path: '/settings/conexiones-ia',
        element: (
          <GuardedPage permission="mcp.connect">
            <ConexionesIaPage />
          </GuardedPage>
        ),
      },
      // Legacy · fusionada en /settings/mi-ia (decisión 2026-05-25).
      {
        path: '/settings/vocabulary',
        element: <Navigate to="/settings/mi-ia" replace />,
      },
      // Centro de ayuda · Anexo E·E5 · glosario + guía de secciones.
      // Sin permiso: cualquier usuario logueado puede consultarla.
      { path: '/ayuda', element: <HelpCenterPage /> },
    ],
  },
  {
    path: '*',
    handle: { layout: 'gesture' } satisfies RouteLayoutHandle,
    element: <NotFoundPage />,
    errorElement: <RouteErrorBoundary />,
  },
];

export const router = createBrowserRouter(routes);
